/**
 * Notification bell + dropdown feed. Shared by the vendor console and the user app
 * (the /user/notifications endpoint resolves the recipient from the token). Pass the
 * route a booking notification should deep-link to.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Loader2 } from 'lucide-react';
import { use_notifications, use_unread_count, use_notification_actions } from '@/features/notifications/hooks';
import { use_session_store } from '@/lib/session-store';
import type { NotificationItem } from '@/features/notifications/types';

function rel_time(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export function NotificationBell({ booking_path }: { booking_path: string }) {
  const [open, set_open] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const role = use_session_store((s) => s.role);
  const notifications_path = role === 'vendor' ? '/vendor/app/notifications' : '/app/notifications';
  const count_q = use_unread_count();
  const list_q = use_notifications(open);
  const { mark_read, mark_all_read } = use_notification_actions();

  const count = count_q.data ?? 0;
  const items = list_q.data ?? [];

  useEffect(() => {
    function on_click(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) set_open(false);
    }
    if (open) document.addEventListener('mousedown', on_click);
    return () => document.removeEventListener('mousedown', on_click);
  }, [open]);

  function on_item(n: NotificationItem) {
    if (!n.read_at) mark_read.mutate(n.id);
    set_open(false);
    if (n.booking_id) navigate(booking_path);
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => set_open((o) => !o)}
        aria-label="Notifications"
        className="relative rounded-button p-2 text-muted-foreground transition-colors hover:text-foreground"
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white"
            data-testid="notification-badge"
          >
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 z-30 mt-2 w-80 overflow-hidden rounded-card border border-border bg-card shadow-xl"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-sm font-semibold">Notifications</span>
            {count > 0 && (
              <button
                type="button"
                onClick={() => mark_all_read.mutate()}
                className="flex items-center gap-1 text-xs text-primary hover:underline"
                data-testid="notification-mark-all"
              >
                <Check className="h-3 w-3" /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {list_q.isLoading ? (
              <div className="flex items-center justify-center p-6 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : items.length === 0 ? (
              <p className="p-6 text-center text-sm text-muted-foreground" data-testid="notification-empty">
                You're all caught up.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {items.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => on_item(n)}
                      className={`flex w-full flex-col gap-0.5 px-3 py-2.5 text-left transition-colors hover:bg-muted/50 ${n.read_at ? '' : 'bg-primary/5'}`}
                      data-testid={`notification-item-${n.id}`}
                    >
                      <div className="flex items-center gap-2">
                        {!n.read_at && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
                        <span className="text-sm font-semibold">{n.title}</span>
                        <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">{rel_time(n.created_at)}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{n.body}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              set_open(false);
              navigate(notifications_path);
            }}
            className="block w-full border-t border-border px-3 py-2 text-center text-xs font-medium text-primary hover:bg-muted/50"
            data-testid="notification-view-all"
          >
            View all
          </button>
        </div>
      )}
    </div>
  );
}
